import React, { useEffect, useRef, useState } from 'react'
import i18n from 'i18next'
import { useTranslation } from 'react-i18next'
import { Checkbox, Label } from '@rebass/forms'
import { useMutation } from '@apollo/client'
import SBox from 'components/opinionless/SBox'
import SButton from 'components/opinionless/SButton'
import SText from 'components/opinionless/SText'
import { StyledPatternImage } from 'styles/styledImage'
import { StyledSTextButton, StyledSFlex } from './styles'
import STooltip from 'components/opinionless/STooltip'
import SFlex from 'components/opinionless/SFlex'
import SInputError from 'components/opinionless/SInputError'
import { StyledSInput, StyledSInputLabel } from './styles'
import { useRouter } from 'next/router'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'

import { checkboxes } from 'constants/checkboxes'
import {
  sanitizeNumber,
  calculateYCTC,
  getCalEITCCredits,
  calculateFederalEITC,
  calculateCTC,
  isEligibleForFederalCTC2026,
} from 'utils/calculatorUtils'
import { ga4_buttonClicked, ga4_inputChanged } from 'utils/ga4'
import { CREATE_TAX_INPUT } from 'graphql/mutations/mutations'

import EstimateResults from './estimateResults'

interface Conditions {
  usCitizenSsn: boolean
  taxesWithItin: boolean
  livedInUs6Months: boolean
  livedInCa6Months: boolean
  notDependent: boolean
  noInvestmentIncomeOver11k: boolean
  hasChildUnder6: boolean
  fosterCareAfter13: boolean
  spouseSsn: boolean
}

interface FormErrors {
  caIncome?: string
  filingStatus?: string
  totalIncome?: string
  qualifyingChildren?: string
  qualifyingChildrenUnder17?: string
  ageOnDec31?: string
  checkboxError?: string
}

interface FormData {
  caIncome: string
  language: string
  filingStatus: string
  totalIncome: string
  qualifyingChildren: string
  qualifyingChildrenUnder17: string
  ageOnDec31: string
  conditions: Conditions
  errors: FormErrors
  source?: string
}

const YEAR = 2025
const STATE = 'California'

const Calculator: React.FC = () => {
  const bottomRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation('common')
  const router = useRouter()
  const [interactedFields, setInteractedFields] = useState<Set<string>>(
    new Set()
  )

  const initialized = useRef(false)

  const handleDownloadPDF = async () => {
    const input = document.getElementById('estimate-results') // Ensure your component has this ID
    if (!input) return

    const canvas = await html2canvas(input)
    const imgData = canvas.toDataURL('image/png')

    const pdf = new jsPDF()
    pdf.addImage(imgData, 'PNG', 10, 10, 180, 0)
    pdf.save('estimate_results.pdf')
  }

  const useResizeObserver = (ref) => {
    useEffect(() => {
      if (!ref.current) return

      let resizeTimeout = null
      const DEBOUNCE_DELAY = 150

      const sendHeight = () => {
        if (ref.current) {
          const height = ref.current.scrollHeight
          window.parent.postMessage({ type: 'resize', height }, '*')
        }
      }

      const handleResize = () => {
        clearTimeout(resizeTimeout)
        resizeTimeout = setTimeout(sendHeight, DEBOUNCE_DELAY)
      }

      const resizeObserver = new ResizeObserver(() => {
        sendHeight()
      })

      resizeObserver.observe(ref.current)

      sendHeight()

      window.addEventListener('resize', handleResize)

      return () => {
        window.removeEventListener('resize', handleResize)
        resizeObserver.disconnect()
        clearTimeout(resizeTimeout)
      }
    }, [ref])
  }

  const [formData, setFormData] = useState<FormData>({
    caIncome: '',
    language: 'en',
    filingStatus: '',
    totalIncome: '',
    qualifyingChildren: '',
    qualifyingChildrenUnder17: '',
    ageOnDec31: '',
    conditions: {
      usCitizenSsn: false,
      taxesWithItin: false,
      livedInUs6Months: false,
      livedInCa6Months: false,
      notDependent: false,
      noInvestmentIncomeOver11k: false,
      hasChildUnder6: false,
      fosterCareAfter13: false,
      spouseSsn: false,
    },
    errors: {},
  })

  const [loading, setLoading] = useState(false)
  const [calEITCValue, setCalEITCValue] = useState<number>(0)
  const [federalEITCValue, setFederalEITCValue] = useState<number>(0)
  const [yctcValue, setYCTCValue] = useState<number>(0)
  const [fytcValue, setFYTCValue] = useState<number>(0)
  const [ctcValue, setCtcValue] = useState<number>(0)
  const [showEstimate, setShowEstimate] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  useResizeObserver(containerRef)

  const [createTaxInput] = useMutation(CREATE_TAX_INPUT)

  useEffect(() => {
    let beaconSent = false

    const sendBeaconIfNeeded = () => {
      if (beaconSent) return
      beaconSent = true

      const hasDataEntered = Object.entries(formData).some(([key, value]) => {
        if (key === 'language') return false
        if (typeof value === 'string') return value.trim() !== ''
        if (typeof value === 'number') return !isNaN(value)
        return false
      })
      const hasCheckedCheckbox = Object.values(formData.conditions).some(
        Boolean
      )

      if (!showEstimate && (hasDataEntered || hasCheckedCheckbox)) {
        const url = `${process.env.NEXT_PUBLIC_ENDPONT}/api/log-unsuccessful-tax-input/`
        const payload = {
          ...formData,
          caIncome: formData.caIncome ? parseFloat(formData.caIncome) : null,
          totalIncome: formData.totalIncome
            ? parseFloat(formData.totalIncome)
            : null,
          qualifyingChildren: formData.qualifyingChildren
            ? parseInt(formData.qualifyingChildren, 10)
            : null,
          qualifyingChildrenUnder17: formData.qualifyingChildrenUnder17
            ? parseInt(formData.qualifyingChildrenUnder17, 10)
            : null,
          ageOnDec31: formData.ageOnDec31
            ? parseInt(formData.ageOnDec31, 10)
            : null,
        }

        const blob = new Blob([JSON.stringify(payload)], {
          type: 'application/json',
        })
        navigator.sendBeacon(url, blob)
      }
    }

    const handleBeforeUnload = () => {
      sendBeaconIfNeeded()
    }

    const handlePageHide = (event) => {
      if (!event.persisted) {
        sendBeaconIfNeeded()
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        sendBeaconIfNeeded()
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    if (window.innerWidth < 900) {
      window.addEventListener('pagehide', handlePageHide)
      document.addEventListener('visibilitychange', handleVisibilityChange)
    }
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      if (window.innerWidth < 900) {
        window.removeEventListener('pagehide', handlePageHide)
        document.removeEventListener('visibilitychange', handleVisibilityChange)
      }
    }
  }, [showEstimate, formData])

  useEffect(() => {
    i18n.changeLanguage(formData.language)
  }, [formData.language])

  useEffect(() => {
    setFormData((prev) => ({ ...prev, errors: {} }))
  }, [t])

  useEffect(() => {
    if (!initialized.current && Object.keys(router.query).length > 0) {
      const newFormData = { ...formData }

      Object.entries(router.query).forEach(([key, value]) => {
        if (key in newFormData.conditions) {
          newFormData.conditions[key] = value === 'true'
        } else if (key in newFormData) {
          newFormData[key] = value
        }
      })

      if (router.query.source) {
        newFormData.source = router.query.source as string
      }

      setFormData(newFormData)
      initialized.current = true
    }
  }, [router.query])

  const handleCheckboxChange = (name: string, checked: boolean) => {
    ga4_inputChanged(name, checked)
    setFormData((prev) => {
      const updatedConditions = { ...prev.conditions, [name]: checked }
      if (name === 'usCitizenSsn' && checked) {
        updatedConditions.taxesWithItin = false
      } else if (name === 'taxesWithItin' && checked) {
        updatedConditions.usCitizenSsn = false
      }
      return { ...prev, conditions: updatedConditions }
    })
  }

  const handleInputChange = (name: string, value: string) => {
    let sanitizedValue = value

    if (
      name === 'caIncome' ||
      name === 'totalIncome' ||
      name === 'ageOnDec31'
    ) {
      sanitizedValue = value.replace(/\D/g, '')
    }

    setFormData((prev) => ({
      ...prev,
      [name]: sanitizedValue,
    }))

    setInteractedFields((prev) => {
      if (!prev.has(name)) {
        if (window?.['plausible']) {
          window?.['plausible'](`First Interaction - ${name}`)
        }
        return new Set(prev).add(name)
      }
      return prev
    })
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target as HTMLInputElement

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked
      handleCheckboxChange(name, checked)

      if (
        (name === 'usCitizenSsn' || name === 'taxesWithItin') &&
        !interactedFields.has('citizenOrItin')
      ) {
        if (window?.['plausible']) {
          window?.['plausible'](
            `First Interaction - usCitizenSsn / taxesWithItin`
          )
        }
        setInteractedFields((prev) => new Set(prev).add('citizenOrItin'))
      }
    } else {
      handleInputChange(name, value)
    }
  }

  const validateForm = (): FormErrors => {
    const newErrors: FormErrors = {}
    if (!formData.caIncome) {
      newErrors.caIncome = t('required')
    } else if (!/^\d+$/.test(formData.caIncome)) {
      newErrors.caIncome = t('invalidNumber')
    }
    if (!formData.filingStatus) newErrors.filingStatus = t('required')
    if (!formData.totalIncome) {
      newErrors.totalIncome = t('required')
    } else if (!/^\d+$/.test(formData.totalIncome)) {
      newErrors.totalIncome = t('invalidNumber')
    }
    if (!formData.qualifyingChildren)
      newErrors.qualifyingChildren = t('required')
    if (!formData.ageOnDec31) {
      newErrors.ageOnDec31 = t('required')
    } else if (!/^\d+$/.test(formData.ageOnDec31)) {
      newErrors.ageOnDec31 = t('invalidNumber')
    }
    if (
      !formData.conditions.usCitizenSsn &&
      !formData.conditions.taxesWithItin
    ) {
      newErrors.checkboxError = t('selectOneOfTheAbove')
    }

    if (!formData.qualifyingChildrenUnder17)
      newErrors.qualifyingChildrenUnder17 = t('required')
    return newErrors
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const newErrors = validateForm()
    setFormData((prev) => ({ ...prev, errors: newErrors }))

    if (Object.keys(newErrors).length > 0) {
      console.log('Form has errors. Mutation not called.')
      return
    }

    setLoading(true)

    try {
      const income = sanitizeNumber(formData.caIncome)
      const totalIncome = sanitizeNumber(formData.totalIncome)
      const [credit1, credit2, credit3, credit4] = getCalEITCCredits(income)

      const isEligibleForCalEITC =
        (formData.conditions.usCitizenSsn ||
          formData.conditions.taxesWithItin) &&
        formData.conditions.livedInCa6Months &&
        formData.conditions.noInvestmentIncomeOver11k &&
        (parseInt(formData.qualifyingChildren, 10) > 0 ||
          (formData.conditions.notDependent &&
            parseInt(formData.ageOnDec31, 10) >= 18 &&
            formData.filingStatus !== 'married_separately'))

      const isEligibleForFederalEITC =
        formData.conditions.usCitizenSsn &&
        formData.conditions.noInvestmentIncomeOver11k &&
        (parseInt(formData.qualifyingChildren, 10) > 0 ||
          (parseInt(formData.ageOnDec31, 10) >= 25 &&
            parseInt(formData.ageOnDec31, 10) <= 64 &&
            formData.conditions.notDependent &&
            formData.conditions.livedInUs6Months))

      const isEligibleForYCTC = isEligibleForCalEITC
        ? parseInt(formData.qualifyingChildren, 10) > 0 &&
          formData.conditions.hasChildUnder6
        : income === 0 &&
          parseInt(formData.qualifyingChildren, 10) > 0 &&
          formData.conditions.hasChildUnder6

      const isEligibleForFYTC =
        isEligibleForCalEITC &&
        parseInt(formData.ageOnDec31, 10) >= 18 &&
        parseInt(formData.ageOnDec31, 10) <= 25 &&
        formData.conditions.fosterCareAfter13

      const yctcAmount = isEligibleForYCTC ? calculateYCTC(income) : 0
      const fedEITCAmount = isEligibleForFederalEITC
        ? parseFloat(
            calculateFederalEITC(
              totalIncome,
              formData.filingStatus,
              formData.qualifyingChildren
            )
          )
        : 0

      const numChildren = parseInt(formData.qualifyingChildren, 10)
      let calEITCAmount = 0
      if (numChildren === 0) calEITCAmount = credit1
      else if (numChildren === 1) calEITCAmount = credit2
      else if (numChildren === 2) calEITCAmount = credit3
      else if (numChildren >= 3) calEITCAmount = credit4

      const numChildrenUnder17 = parseInt(
        formData.qualifyingChildrenUnder17,
        10
      )

      const isEligibleForFederalCTC = isEligibleForFederalCTC2026({
        conditions: formData.conditions,
        qualifyingChildren: numChildren,
        qualifyingChildrenUnder17: numChildrenUnder17,
        ageOnDec31: parseInt(formData.ageOnDec31, 10),
        filingStatus: formData.filingStatus,
      })

      const ctcAmount = isEligibleForFederalCTC
        ? calculateCTC(totalIncome, numChildrenUnder17, formData.filingStatus)
        : 0

      setCalEITCValue(isEligibleForCalEITC ? calEITCAmount : 0)
      setFYTCValue(isEligibleForFYTC ? 1189 : 0)
      setFederalEITCValue(fedEITCAmount)
      setYCTCValue(yctcAmount)
      setCtcValue(ctcAmount)

      setShowEstimate(true)

      await createTaxInput({
        variables: {
          ...formData,
          ...formData.conditions,
        },
      })
      ga4_buttonClicked('Calculator Form Submission')
      if (window?.['plausible']) {
        window['plausible']('Calculation Successful')
      }

      if (window.innerWidth > 900) {
        setTimeout(() => {
          bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' })
        }, 300)
      } else {
        setTimeout(() => {
          const bottomElement = document.getElementById('bottom-scroll-target')

          if (bottomElement) {
            bottomElement.scrollIntoView({ behavior: 'smooth', block: 'end' })

            document.documentElement.scrollTop =
              document.documentElement.scrollHeight
            document.body.scrollTop = document.body.scrollHeight

            bottomElement.focus()
          }
        }, 300)
      }
    } catch (error) {
      console.error('Error submitting tax input:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <SBox
      as="form"
      ref={containerRef}
      onSubmit={handleSubmit}
      sx={{
        justifyContent: 'center',
        padding: ['20px 24px', '20px 24px', '20px 100px'],
        width: '100%',
        background: '#F6F6F6',
      }}
    >
      <StyledSFlex
        sx={{
          display: 'flex',
          justifyContent: 'center',
          flexDirection: ['column', 'row'],
          flexWrap: ['wrap', 'nowrap'],
          marginTop: '24px',
        }}
      >
        <SBox sx={{ mr: 6, width: ['100%', '100%', '50%'] }}>
          <div
            style={{ fontWeight: 900, fontSize: '32px', marginBottom: '24px' }}
          >
            CalEITC4Me Calculator
          </div>
          <StyledSInput
            as="select"
            name="language"
            value={formData.language}
            onChange={(val) => {
              handleChange(val)
              i18n.changeLanguage(val.target.value)
            }}
            sx={{ width: ['100%', '100%', '50%'] }}
          >
            <option value="en">English</option>
            <option value="es">Spanish</option>
            <option value="hy">Armenian</option>
            <option value="ar">Arabic</option>
            <option value="zh-CN">Chinese Simplified</option>
            <option value="zh-TW">Chinese Traditional</option>
            <option value="fa">Farsi</option>
            <option value="hmn">Hmong</option>
            <option value="km">Khmer</option>
            <option value="ko">Korean</option>
            <option value="ru">Russian</option>
            <option value="tl">Tagalog</option>
            <option value="vi">Vietnamese</option>
          </StyledSInput>

          <StyledSInputLabel>
            <SFlex alignItems="center" sx={{ width: 'inherit' }}>
              {t('incomeLabel', { YEAR, STATE })}
              <STooltip
                content={t('incomeHelp', { YEAR, STATE })}
                sx={{ marginLeft: ['12px', '12px', '12px'] }}
              />
            </SFlex>
          </StyledSInputLabel>
          <StyledSInput
            id="caIncome"
            name="caIncome"
            type="currency"
            placeholder="$"
            value={formData.caIncome}
            onChange={handleChange}
          />
          <SInputError forElementId="caIncome">
            {formData.errors.caIncome}
          </SInputError>

          <StyledSInputLabel>
            <SFlex alignItems="center" sx={{ width: 'inherit' }}>
              {t('filingLabel')}
              <STooltip content={t('filingHelp')} sx={{ marginLeft: '12px' }} />
            </SFlex>
          </StyledSInputLabel>
          <StyledSInput
            as="select"
            id="filingStatus"
            name="filingStatus"
            value={formData.filingStatus}
            onChange={handleChange}
          >
            <option value="">{t('optionSelectOne')}</option>
            <option value="single">{t('optionSingle')}</option>
            <option value="married">{t('optionMarriedJoint')}</option>
            <option value="married_separately">
              {t('optionMarriedSeparate')}
            </option>
            <option value="qualifying_surviving_spouse">
              {t('optionSurvivingSpouse')}
            </option>
            <option value="head_of_household">
              {t('optionHeadHousehold')}
            </option>
          </StyledSInput>
          <SInputError forElementId="filingStatus">
            {formData.errors.filingStatus}
          </SInputError>

          <StyledSInputLabel>
            <SFlex alignItems="center" sx={{ width: 'inherit' }}>
              {t('childrenLabel')}
              <STooltip
                content={t('childrenHelp', { YEAR, STATE })}
                sx={{ marginLeft: '12px' }}
              />
            </SFlex>
          </StyledSInputLabel>

          <StyledSInput
            id="qualifyingChildren"
            as="select"
            name="qualifyingChildren"
            value={formData.qualifyingChildren}
            onChange={handleChange}
          >
            <option value="">{t('optionSelectOne')}</option>
            <option value="0">0</option>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3+">{t('optionThreeOrMore')}</option>
          </StyledSInput>

          <SInputError forElementId="qualifyingChildren">
            {formData.errors.qualifyingChildren}
          </SInputError>

          <StyledSInputLabel>
            <SFlex alignItems="center" sx={{ width: 'inherit' }}>
              {t('qualifyingChildrenUnder17')}
              <STooltip
                content={t('neededForFederalCTC', { YEAR, STATE })}
                sx={{ marginLeft: '12px' }}
              />
            </SFlex>
          </StyledSInputLabel>
          <StyledSInput
            id="qualifyingChildrenUnder17"
            as="select"
            name="qualifyingChildrenUnder17"
            value={formData.qualifyingChildrenUnder17}
            onChange={handleChange}
          >
            <option value="">{t('optionSelectOne')}</option>
            <option value="0">0</option>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3+">{t('optionThreeOrMore')}</option>
          </StyledSInput>
          <SInputError forElementId="qualifyingChildrenUnder17">
            {formData.errors.qualifyingChildrenUnder17}
          </SInputError>

          <StyledSInputLabel>
            <SFlex alignItems="center" sx={{ width: 'inherit' }}>
              {t('totalLabel', { YEAR })}
              <STooltip
                content={t('totalHelp', { YEAR, STATE })}
                sx={{ marginLeft: '12px' }}
              />
            </SFlex>
          </StyledSInputLabel>
          <StyledSInput
            id="totalIncome"
            name="totalIncome"
            type="currency"
            placeholder="$"
            value={formData.totalIncome}
            onChange={handleChange}
          />
          <SInputError forElementId="totalIncome">
            {formData.errors.totalIncome}
          </SInputError>

          <StyledSInputLabel>
            <SFlex alignItems="center" sx={{ width: 'inherit' }}>
              {t('ageLabel', { YEAR })}
              <STooltip
                content={t('ageHelp', { YEAR, STATE })}
                sx={{ marginLeft: '12px' }}
              />
            </SFlex>
          </StyledSInputLabel>
          <StyledSInput
            id="ageOnDec31"
            name="ageOnDec31"
            type="currency"
            sx={{ width: ['100%', '100%', '50%'] }}
            value={formData.ageOnDec31}
            onChange={handleChange}
          />
          <SInputError forElementId="ageOnDec31">
            {formData.errors.ageOnDec31}
          </SInputError>
        </SBox>

        <SBox sx={{ ml: [0, 0, 6], mt: ['0', '0', '64px'], pr: ['14px', '0'] }}>
          <SText
            id="selectLabel"
            fontSize={['20px', '20px', '24px']}
            mb="24px"
            fontWeight={700}
          >
            {t('selectLabel')}
          </SText>

          {checkboxes.map(({ name, label }, index) => (
            <SBox sx={{ position: 'relative' }}>
              <SBox
                key={name}
                sx={{ position: 'relative', marginBottom: '16px' }}
              >
                <Label
                  sx={{
                    display: 'flex',
                    fontSize: ['16px', '16px', '18px'],
                    lineHeight: '1.4',
                    minHeight: '16px',
                  }}
                >
                  <Checkbox
                    name={name}
                    checked={formData.conditions[name]}
                    onChange={handleChange}
                    sx={{ marginRight: '10px', flexShrink: 0 }}
                    color="#202020"
                    id={name}
                  />
                  <SBox sx={{ flex: 1, maxWidth: ['100%', '100%', '600px'] }}>
                    {t(label, { YEAR, STATE })}
                  </SBox>
                </Label>
              </SBox>
              {index === 1 && formData.errors?.checkboxError && (
                <SText
                  color={'inputError'}
                  fontSize={10}
                  fontWeight={'bold'}
                  sx={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formData.errors.checkboxError}
                </SText>
              )}
            </SBox>
          ))}

          <SBox
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: 'auto',
              alignItems: 'flex-start',
              marginTop: 4,
            }}
          >
            <SButton
              type="submit"
              sx={{
                backgroundColor: 'black',
                color: 'white',
                marginTop: '12px',
              }}
              onClick={() => {
                if (window?.['plausible']) {
                  window['plausible']('Submit Button Clicked')
                }
              }}
              loading={loading}
            >
              <StyledSTextButton fontSize={['20px', '20px', '24px']}>
                {t('buttonLabel')}
              </StyledSTextButton>
            </SButton>
            {/* <SButton onClick={handleDownloadPDF} sx={{ marginTop: '12px' }}>
              Download PDF
            </SButton> */}
            <StyledPatternImage
              src="/powered-by.svg"
              sx={{ height: '40px', marginTop: '20px', cursor: 'pointer' }}
              onClick={() => {
                window.location.href = 'http://f4gi.org/'
              }}
            />
          </SBox>
          <EstimateResults
            showEstimate={showEstimate}
            t={t}
            calEITCValue={calEITCValue}
            federalEITCValue={federalEITCValue}
            yctcValue={yctcValue}
            fytcValue={fytcValue}
            ctcValue={ctcValue}
          />
        </SBox>
        <div ref={bottomRef} />
        <div
          id="bottom-scroll-target"
          style={{ height: '10px', visibility: 'hidden' }}
        />
      </StyledSFlex>
    </SBox>
  )
}

export default Calculator
